/**
 * HolderSelector — search-as-you-type user picker for asset reassignment.
 *
 * Usage:
 *   <HolderSelector value={holderId} onChange={setHolderId} />
 */

import { useState, useEffect, useRef } from 'react';
import { Search, User, X, Loader2 } from 'lucide-react';
import apiClient from '../api/client';
import { cn } from '../utils/cn';

const ROLE_LABELS = {
  ADMIN: 'Admin',
  GA: 'General Affairs',
  FINANCE: 'Finance',
  EXECUTIVE: 'Executive',
  VIEWER: 'Employee',
};

export function HolderSelector({ value, onChange, placeholder = 'Search employee...' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // { id, name, email, role }
  const inputRef = useRef();
  const dropdownRef = useRef();
  const debounceRef = useRef();

  // Resolve an already-set value (e.g. restored from a URL filter) to its
  // display name on mount, rather than showing an empty search box while
  // a filter is silently active underneath it.
  useEffect(() => {
    if (value && selected?.id !== value) {
      apiClient
        .get('/api/holders/search', { params: { id: value } })
        .then(r => setSelected(r.data[0] ?? null))
        .catch(() => {});
    }
    if (!value) {
      setSelected(null);
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Search with debounce
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      apiClient.get('/api/holders/search', { params: { q: query, limit: 10 } })
        .then(r => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(user) {
    setSelected(user);
    onChange(user.id);
    setOpen(false);
    setQuery('');
  }

  function handleClear() {
    setSelected(null);
    onChange(null);
    setQuery('');
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected state */}
      {selected ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white px-3 py-2.5">
          <div className="h-7 w-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold shrink-0">
            {selected.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{selected.name}</p>
            <p className="text-xs text-slate-400 truncate">{selected.email}</p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-600 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        /* Search input */
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
          )}
        </div>
      )}

      {/* Dropdown */}
      {open && !selected && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {loading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400 text-center">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400 text-center">
              {query ? 'No employees found' : 'Type to search employees'}
            </div>
          ) : (
            <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100">
              {results.map(user => (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(user)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold shrink-0">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs text-slate-400">
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                      {user.assetCount > 0 && (
                        <p className="text-xs text-slate-300">{user.assetCount} assets</p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
